//dependencies
import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

//components
import Button from "../../../../../components/Button";
import Pagination from "../../../../../components/Pagination";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import ReviewsOfMyTeamFiltersPanel, { ReviewsOfMyTeamFilters } from "./components/ReviewsOfMyTeamFilterPanel";
import ReviewsOfMyTeamReadableFilters from "./components/ReviewsOfMyTeamReadableFilters";
import ReviewsOfMyTeamTableCols from "./components/ReviewsOfMyTeamTableCols";

//hooks
import { useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
import useUserReviewsNavLinksAndPermissions from "../../hooks/useUserReviewsNavLinksAndPermissions";

//services
import { SERVICE_REVIEW } from "../../../../../api/services/defs/search-service";
import { Review } from "../../../../../api/services/searchServiceResultTypes/reviews";

//utils
import { getFiltersForSearchService } from "./utils";

//page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { REVIEWS_TABLE_LIMIT, USER_REVIEWS_CONFIG } from "../../constants";

const ReviewsOfMyTeam = () => {
	const {
		activePage,
		navLinks,
		directReports,
		isEasyAddEnabled,
		employeeId,
		userCanCreateReviews,
		userCanAdd360Forms,
	} = useUserReviewsNavLinksAndPermissions();
	const { handleQueryChange } = useSearchQueryParams({ schema: USER_REVIEWS_CONFIG.queryParametersSchema });

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<ReviewsOfMyTeamFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const searchFilters = useMemo(
		() => getFiltersForSearchService(filters, directReports, debouncedSearchValue),
		[JSON.stringify(filters), JSON.stringify(directReports), debouncedSearchValue]
	);

	const { data, isLoading } = useSearchService<Review>({
		service: SERVICE_REVIEW,
		filters: searchFilters,
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Reviews of Me. Please try again.",
	});

	//prefetch 3 pages ahead and 1 back
	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: searchFilters,
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		pageNumber > 1 ? 1 : 0
	);

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: searchFilters,
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		3
	);

	const forms = data?.results || [];
	const totalForms = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const additionalFiltersCount = Object.keys(filters).length;
	const emptyTableState = <EmptySearchTableMessage mainText="No Reviews Found" />;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search reviews..."
		/>
	);

	const drawer = (
		<ReviewsOfMyTeamFiltersPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = <ReviewsOfMyTeamReadableFilters filters={filters} setFilters={setFilters} />;

	const mainContent = (
		<div>
			<ScrollableTable
				columns={ReviewsOfMyTeamTableCols(isEasyAddEnabled)}
				rows={forms}
				isLoading={isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(columnID, nextDirection) =>
					setSort({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(totalForms / REVIEWS_TABLE_LIMIT)}
				currentPage={pageNumber}
				onClick={(pageNumber: number) => setPageNumber(pageNumber)}
			/>
		</div>
	);

	const actionButtons = [
		userCanAdd360Forms && (
			<Button data-testid="create360FormButton" onClick={() => handleQueryChange({ modal: "add360Form" })}>
				Add 360 Form
			</Button>
		),
		userCanCreateReviews && (
			<Button
				data-testid="createReviewButton"
				onClick={() => handleQueryChange({ modal: "createReview" })}
				color="green"
				icon={faPlus}
			>
				Create Review
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeId}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks}
			activeNavigationLinkKey={activePage}
		/>
	);
};

export default ReviewsOfMyTeam;
